/**
 * Expiration utilities for parsing and classifying expiration dates shown in the Space grid.
 *
 * Responsibilities:
 * - Extract a leading ISO date (YYYY-MM-DD) from strings that may include annotations, e.g. "2025-01-01 (short)".
 * - Compute days remaining from today and classify into a semantic status.
 *
 * Notes:
 * - All calculations are done at day precision (00:00 local time) to avoid time-of-day drift.
 */
export type ExpStatus = "expired" | "soon" | "ok" | "none";

/**
 * Extract the leading ISO date (YYYY-MM-DD) from a raw expiration string.
 *
 * Examples:
 *  - "2025-01-01 (short)" => "2025-01-01"
 *  - "–" or undefined => null
 *
 * @param value Raw expiration string potentially containing annotations.
 * @returns ISO date string (YYYY-MM-DD) or null when not present/parsable.
 */
export function extractISODate(value?: string): string | null {
  if (!value) return null;
  const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

/**
 * Calculate the whole number of days from today (00:00 local time) until a target ISO date.
 * Negative values indicate the target date is in the past.
 *
 * @param dateStr ISO date string (YYYY-MM-DD).
 * @returns Number of whole days until the date, or null if the date is invalid.
 */
export function daysUntil(dateStr: string): number | null {
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((d.getTime() - today.getTime()) / msPerDay);
}

/**
 * Determine the expiration status from a raw expiration value.
 *
 * Logic:
 * - "expired" when days < 0
 * - "soon" when 0 <= days <= 5
 * - "ok" when days > 5
 * - "none" when no valid date can be extracted
 *
 * @param value Raw expiration string possibly containing annotations.
 * @returns ExpStatus classification for display and styling.
 */
export function getExpirationStatus(value: string): ExpStatus {
  const iso = extractISODate(value);
  if (!iso) return "none";
  const left = daysUntil(iso);
  if (left === null) return "none";
  if (left < 0) return "expired";
  if (left <= 5) return "soon";
  return "ok";
}
